import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { getFinanceAverageRevenueHistory, getAllFactories } from '../../api';
import { format, subDays, addDays } from 'date-fns';
import './ChangelogPage.css';

const ChangelogPage = () => {
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [error, setError] = useState(null);
    const [factories, setFactories] = useState([]);
    const [selectedFactories, setSelectedFactories] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'changeTimestamp', direction: 'desc' });

    useEffect(() => {
        loadFactories();
    }, []);

    useEffect(() => {
        loadHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, selectedFactories, sortConfig]);

    const loadFactories = async () => {
        try {
            const response = await getAllFactories();
            setFactories(response.data || []);
        } catch (err) {
            console.error('Ошибка при загрузке заводов:', err);
        }
    };

    const handlePreviousDay = () => {
        const currentDate = new Date(selectedDate);
        const previousDate = subDays(currentDate, 1);
        setSelectedDate(format(previousDate, 'yyyy-MM-dd'));
    };

    const handleNextDay = () => {
        const currentDate = new Date(selectedDate);
        const nextDate = addDays(currentDate, 1);
        setSelectedDate(format(nextDate, 'yyyy-MM-dd'));
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (columnKey) => {
        if (sortConfig.key !== columnKey) return '';
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    const sortData = (data) => {
        if (!data || data.length === 0) return data;
        
        const sortedData = [...data];
        sortedData.sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'changeTimestamp':
                    aValue = new Date(a.changeTimestamp);
                    bValue = new Date(b.changeTimestamp);
                    break;
                case 'operationType':
                    aValue = getOperationTypeText(a.operationType);
                    bValue = getOperationTypeText(b.operationType);
                    break;
                case 'editorId':
                    aValue = a.editorId || '';
                    bValue = b.editorId || '';
                    break;
                case 'editorType':
                    aValue = getEditorTypeText(a.editorType);
                    bValue = getEditorTypeText(b.editorType);
                    break;
                case 'factoryName':
                    const newA = parseRevenueData(a.newRevenueData);
                    const newB = parseRevenueData(b.newRevenueData);
                    aValue = newA?.factoryName || 'Неизвестно';
                    bValue = newB?.factoryName || 'Неизвестно';
                    break;
                case 'period':
                    const aData = parseRevenueData(a.newRevenueData) || parseRevenueData(a.oldRevenueData);
                    const bData = parseRevenueData(b.newRevenueData) || parseRevenueData(b.oldRevenueData);
                    aValue = aData?.month && aData?.year ? `${aData.year}-${aData.month.toString().padStart(2, '0')}` : '';
                    bValue = bData?.month && bData?.year ? `${bData.year}-${bData.month.toString().padStart(2, '0')}` : '';
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        
        return sortedData;
    };

    const loadHistoryData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Формируем параметры для запроса - фильтруем по выбранной дате
            const startDate = new Date(selectedDate);
            startDate.setHours(0, 0, 0, 0);
            
            const endDate = new Date(selectedDate);
            endDate.setHours(23, 59, 59, 999);
            
            const params = {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                page: 0,
                size: 100,
                sort: 'changeTimestamp,desc'
            };
            
            // Добавляем фильтрацию по заводам, если они выбраны
            if (selectedFactories.length > 0) {
                params.factoryIds = selectedFactories.map(factory => factory.value);
            }
            
            const response = await getFinanceAverageRevenueHistory(params);
            const rawData = response.data.content || [];
            setHistoryData(sortData(rawData));
        } catch (err) {
            console.error('Ошибка при загрузке истории изменений:', err);
            setError('Не удалось загрузить историю изменений');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getOperationTypeText = (type) => {
        switch (type) {
            case 'CREATE': return 'Создание';
            case 'UPDATE': return 'Изменение';
            case 'DELETE': return 'Удаление';
            default: return type;
        }
    };

    const getEditorTypeText = (type) => {
        switch (type) {
            case 'MASTER': return 'Мастер';
            case 'MANAGER': return 'Менеджер';
            case 'SYSTEM': return 'Система';
            default: return type;
        }
    };

    const parseRevenueData = (jsonData) => {
        try {
            return JSON.parse(jsonData);
        } catch {
            return null;
        }
    };

    const renderRevenueChange = (oldData, newData) => {
        const old = parseRevenueData(oldData);
        const newRevenueData = parseRevenueData(newData);
        
        if (!old && !newRevenueData) return '-';
        
        const oldRevenue = old?.averageRevenue || 0;
        const newRevenue = newRevenueData?.averageRevenue || 0;
        const difference = newRevenue - oldRevenue;
        
        return (
            <div>
                <div>Было: {oldRevenue.toFixed(2)}</div>
                <div>Стало: {newRevenue.toFixed(2)}</div>
                {difference !== 0 && (
                    <div className={`revenue-difference ${difference > 0 ? 'positive' : 'negative'}`}>
                        {difference > 0 ? '+' : ''}{difference.toFixed(2)}
                    </div>
                )}
            </div>
        );
    };

    const factoryOptions = factories.map(factory => ({
        value: factory.id,
        label: factory.name
    }));

    return (
        <div className="changelog-page">
            <div className="changelog-container">
                <h1 className="changelog-title">Журнал изменений</h1>
                
                <div className="filters-container">
                    <div className="date-filter">
                        <button 
                            className="date-nav-button date-nav-prev"
                            onClick={handlePreviousDay}
                            title="Предыдущий день"
                        >
                            ‹
                        </button>
                        
                        <div className="date-input-container">
                            <label htmlFor="date-select">Дата:</label>
                            <input
                                id="date-select"
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="date-input"
                            />
                        </div>
                        
                        <button 
                            className="date-nav-button date-nav-next"
                            onClick={handleNextDay}
                            title="Следующий день"
                        >
                            ›
                        </button>
                    </div>
                    
                    <div className="factory-filter">
                        <label htmlFor="factories-select">Заводы:</label>
                        <Select
                            id="factories-select"
                            options={factoryOptions}
                            isMulti
                            value={selectedFactories}
                            onChange={setSelectedFactories}
                            placeholder="Выберите заводы"
                            className="factory-select"
                            noOptionsMessage={() => 'Заводы не найдены'}
                        />
                    </div>
                </div>

                <div className="changelog-content">
                    {loading ? (
                        <div className="loading">Загрузка...</div>
                    ) : error ? (
                        <div className="error">{error}</div>
                    ) : historyData.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">📋</div>
                            <h2>Нет изменений за выбранную дату</h2>
                            <p>За {selectedDate} изменений в среднедневной выручке не было</p>
                        </div>
                    ) : (
                        <div className="history-table-container">
                            <table className="history-table">
                                <thead>
                                    <tr>
                                        <th 
                                            className="sortable-header"
                                            onClick={() => handleSort('changeTimestamp')}
                                        >
                                            Дата и время{getSortIndicator('changeTimestamp')}
                                        </th>
                                        <th 
                                            className="sortable-header"
                                            onClick={() => handleSort('operationType')}
                                        >
                                            Операция{getSortIndicator('operationType')}
                                        </th>
                                        <th 
                                            className="sortable-header"
                                            onClick={() => handleSort('editorId')}
                                        >
                                            Пользователь{getSortIndicator('editorId')}
                                        </th>
                                        <th 
                                            className="sortable-header"
                                            onClick={() => handleSort('editorType')}
                                        >
                                            Тип пользователя{getSortIndicator('editorType')}
                                        </th>
                                        <th 
                                            className="sortable-header"
                                            onClick={() => handleSort('factoryName')}
                                        >
                                            Завод{getSortIndicator('factoryName')}
                                        </th>
                                        <th 
                                            className="sortable-header"
                                            onClick={() => handleSort('period')}
                                        >
                                            Период{getSortIndicator('period')}
                                        </th>
                                        <th>Изменение выручки</th>
                                        <th>Причина</th>
                                        <th>IP адрес</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyData.map((record) => {
                                        const newRevenueData = parseRevenueData(record.newRevenueData);
                                        const factoryName = newRevenueData?.factoryName || 'Неизвестно';
                                        const month = newRevenueData?.month || parseRevenueData(record.oldRevenueData)?.month;
                                        const year = newRevenueData?.year || parseRevenueData(record.oldRevenueData)?.year;
                                        
                                        return (
                                            <tr key={record.id}>
                                                <td>{formatDate(record.changeTimestamp)}</td>
                                                <td>
                                                    <span className={`operation-type ${record.operationType.toLowerCase()}`}>
                                                        {getOperationTypeText(record.operationType)}
                                                    </span>
                                                </td>
                                                <td>{record.editorId === '0' ? 'менеджер' : record.editorId}</td>
                                                <td>{getEditorTypeText(record.editorType)}</td>
                                                <td>{factoryName}</td>
                                                <td>{month && year ? `${month}.${year}` : '-'}</td>
                                                <td>
                                                    {renderRevenueChange(record.oldRevenueData, record.newRevenueData)}
                                                </td>
                                                <td>{record.changeReason || '-'}</td>
                                                <td>{record.clientIp || '-'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChangelogPage;
